import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AbsoluteCenter,
  Box,
  Collapsible,
  Flex,
  Grid,
  IconButton,
  LinkBox,
  LinkOverlay,
  Skeleton,
  Table,
  Text,
} from "@chakra-ui/react";
import {
  MdPhotoCamera,
  MdHighlightOff,
  MdOutlineQrCodeScanner,
  MdArrowUpward,
  MdArrowDownward,
} from "react-icons/md";

import { EmptyState } from "@/components/ui/empty-state";
import {
  PageContent,
  PageHeader,
  PageLayout,
  PageTitle,
} from "@/components/page";
import {
  useNursery,
  useNurseryStats,
  useObservationDates,
  useObservationStats,
  useTableView,
} from "@/hooks";
import { ITankStats } from "@/types/data";
import PageSectionTitle from "@/components/page/PageSectionTitle";
import HealthIssueCell from "@/components/HealthIssueCell";
import SummaryCard from "@/components/SummaryCard";
import InsightCard from "@/components/InsightCard";
import ObservationsCalendar from "@/components/charts/calendar";
import { GrowthZoneBox, GrowthZoneCell, TimespanFilter } from "@/components/charts";
import {
  TIMESPAN_OPTIONS,
  ITimespan,
  getTimespanInterval,
} from "@/components/charts/timespan";
import { BarChartBox, LineChartBox } from "@/components/charts";
import { CollapsibleTrigger } from "@/components";
import TableViewToggle from "@/components/TableViewToggle";

type RouteParams = { nurseryId: string };

function NurseryDetail() {
  const { nurseryId } = useParams<RouteParams>();
  const { view } = useTableView();
  const [open, setOpen] = useState(true);
  const { data, isPending, error } = useNursery(nurseryId!);
  const { data: stats, isPending: statsIsPending } = useNurseryStats(nurseryId!);
  const [timespan, setTimespan] = useState<ITimespan>("Month");
  const { data: obsDates, isPending: obsDatesIsPending } = useObservationDates(  
    nurseryId!,
  );
  const { data: obsStats, isPending: obsStatsIsPending } = useObservationStats(
    nurseryId!,
    getTimespanInterval(timespan),
    new URLSearchParams({ created_after: TIMESPAN_OPTIONS[timespan] }),
  );

  return (
    <PageLayout>
      <PageHeader backLink="/">
        {data?.name ? `Nursery: ${data.name}` : "Coracle: Coral Intelligence"}
      </PageHeader>
      <PageTitle nurseryName={data?.name} isPending={isPending} />
      <PageContent>
        {error && (
          <EmptyState
            icon={<MdHighlightOff />}
            title="Error"
            description={error.message}
          />
        )}
        <Collapsible.Root defaultOpen onOpenChange={(e) => setOpen(e.open)}>
          <Flex
            alignItems="end"
            w="full"
            justifyContent="space-between"
            mb={{ base: 4, sm: 6 }}
            flexWrap="wrap"
          >
            <TimespanFilter timespan={timespan} setTimespan={setTimespan} />
            <CollapsibleTrigger open={open} />
          </Flex>
          <Collapsible.Content>
            <Grid
              templateColumns="repeat(auto-fit, minmax(8rem, 1fr))"
              gap="2"
              mb="4"
            >
              <InsightCard label="Total Tanks" value={stats?.length} />
              <InsightCard
                label="Total Racks"
                value={stats?.reduce((acc, i) => (acc += i.racks), 0)}
              />
              <InsightCard
                label="Total Fragments"
                value={stats?.reduce((acc, i) => (acc += i.fragments), 0)}
              />
              <InsightCard
                label="Average Growth"
                value={`${stats?.length ? (stats.reduce((acc, i) => (acc += i.growth), 0) / stats.length).toFixed(1) : "0"}%`}
              />
            </Grid>
            <Grid
              templateColumns={{
                base: "1fr",
                sm: "repeat(auto-fit, minmax(18rem, 1fr))",
                md: "20rem repeat(auto-fit, minmax(14rem, 1fr))",
              }}
              gap="2"
              mb="4"
            >
              <Skeleton loading={obsDatesIsPending}>
                <ObservationsCalendar
                  hideBelow="sm"
                  activeDates={
                    obsDates?.map((obs) =>
                      new Date(obs.date).toLocaleDateString(),
                    ) || []
                  }
                />
              </Skeleton>
              <Skeleton loading={obsStatsIsPending}>
                <LineChartBox
                  title="Aggregate Nursery Area"
                  info="The area of all fragments over time"
                  data={obsStats || []}
                  interval={getTimespanInterval(timespan)}
                />
              </Skeleton>
              <Skeleton loading={obsStatsIsPending}>
                <BarChartBox
                  title="Nursery Health Issues"
                  info="Most prevalent health issues in the nursery"
                  data={obsStats || []}
                />
              </Skeleton>
              <Skeleton loading={statsIsPending}>
                {stats &&
                <GrowthZoneBox
                  title="Nursery Growth Zones"
                  fragments_count_0_25={stats.reduce((acc, curr) => acc + curr.fragments_count_0_25, 0)}
                  fragments_count_25_50={stats.reduce((acc, curr) => acc + curr.fragments_count_25_50, 0)}
                  fragments_count_50_75={stats.reduce((acc, curr) => acc + curr.fragments_count_50_75, 0)}
                  fragments_count_75_100={stats.reduce((acc, curr) => acc + curr.fragments_count_75_100, 0)}
                />}
              </Skeleton>
            </Grid>
          </Collapsible.Content>
        </Collapsible.Root>

        <Flex
          mb="1"
          w="full"
          alignItems="center"
          justifyContent="space-between"
        >
          <PageSectionTitle>Tanks</PageSectionTitle>
          <TableViewToggle />
        </Flex>
        {view === "list" && (
          <Table.Root
            interactive
            colorPalette="blue"
            variant="line"
            stickyHeader
          >
            <Table.Header
              textTransform="uppercase"
              fontSize="xs"
              letterSpacing="0.75px"
            >
              <Table.Row
                css={{ "& > *": { color: "gray.400", fontWeight: "semibold" } }}
              >
                <Table.ColumnHeader>Tank</Table.ColumnHeader>
                <Table.ColumnHeader hideBelow="sm">
                  Last Observation
                </Table.ColumnHeader>
                <Table.ColumnHeader hideBelow="sm" textAlign="right">
                  # of Racks
                </Table.ColumnHeader>
                <Table.ColumnHeader hideBelow="sm" textAlign="right">
                  # of Fragments
                </Table.ColumnHeader>
                <Table.ColumnHeader hideBelow="sm">
                  Growth Zone
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">
                  Unhealthy Fragments
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">
                  Growth
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {stats?.map(
                ({
                  tank,
                  racks,
                  fragments,
                  latest_observation_date,
                  unhealthy_fragments,
                  growth,
                  fragments_count_0_25,
                  fragments_count_25_50,
                  fragments_count_50_75,
                  fragments_count_75_100,
                }: ITankStats) => (
                  <LinkBox
                    key={tank.id}
                    position="relative"
                    as={Table.Row}
                    transform="scale(1)"
                    _hover={{ bg: "blue.50" }}
                  >
                    <Table.Cell fontWeight="bold" color="blue.500">
                      <LinkOverlay asChild>
                        <Link to={`tanks/${tank.id}/`}>{tank.name}</Link>
                      </LinkOverlay>
                    </Table.Cell>
                    <Table.Cell hideBelow="sm">
                      {latest_observation_date &&
                        new Date(latest_observation_date).toLocaleString([], {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                    </Table.Cell>
                    <Table.Cell hideBelow="sm" textAlign="right">
                      {racks}
                    </Table.Cell>
                    <Table.Cell hideBelow="sm" textAlign="right">
                      {fragments}
                    </Table.Cell>
                    <Table.Cell hideBelow="sm">
                      <GrowthZoneCell
                        fragments_count_0_25={fragments_count_0_25}
                        fragments_count_25_50={fragments_count_25_50}
                        fragments_count_50_75={fragments_count_50_75}
                        fragments_count_75_100={fragments_count_75_100}
                      />
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      <HealthIssueCell
                        fragments={fragments}
                        unhealthy_fragments={unhealthy_fragments}
                      />
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {growth !== null
                        ? `${Number(growth).toFixed(1).replace(/\.0$/, "")}%`
                        : "-"}
                      <Text display="inline-block" pl="1">
                        {growth > 0 && <MdArrowUpward />}
                        {growth < 0 && <MdArrowDownward />}
                      </Text>
                    </Table.Cell>
                  </LinkBox>
                ),
              )}
            </Table.Body>
          </Table.Root>
        )}
        {view === "grid" && (
          <Grid
            templateColumns={{
              base: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            }}
            gap={[2, 4]}
            mb="4"
          >
            {stats?.map((s: ITankStats) => (
              <SummaryCard
                key={s.tank.id}
                linkTo={`tanks/${s.tank.id}/`}
                title={s.tank.name}
                fragments={s.fragments}
                unhealthyFragments={s.unhealthy_fragments}
                growth={s.growth}
                fragments_count_0_25={s.fragments_count_0_25}
                fragments_count_25_50={s.fragments_count_25_50}
                fragments_count_50_75={s.fragments_count_50_75}
                fragments_count_75_100={s.fragments_count_75_100}
              />
            ))}
          </Grid>
        )}
      </PageContent>
      <Box
        hideFrom="sm"
        zIndex="10"
        textAlign="center"
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        bg="bg.info"
        pt="6"
        h="8"
        borderTopWidth="1px"
        borderColor="blue.100"
      >
        <AbsoluteCenter>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="8"
          >
            <IconButton
              asChild
              bottom="4"
              aria-label="Find tank"
              rounded="full"
              size="xl"
              colorPalette="blue"
              variant="surface"
            >
              <Link to="tanks/search/">
                <MdOutlineQrCodeScanner />
              </Link>
            </IconButton>
            <IconButton
              asChild
              bottom="4"
              aria-label="Take photo"
              rounded="full"
              size="xl"
              colorPalette="blue"
              variant="surface"
            >
              <Link to="observation/">
                <MdPhotoCamera />
              </Link>
            </IconButton>
          </Box>
        </AbsoluteCenter>
      </Box>
    </PageLayout>
  );
}

export default NurseryDetail;
